/**
 * ProgressBar Component
 * Document completion progress indicator
 */

import React from 'react';
import { cn } from '@/lib/utils';
import { Progress } from '@/components/ui/progress';
import { CheckCircle2, Clock, AlertCircle } from 'lucide-react';

/**
 * Get progress color based on percentage
 */
const getProgressColor = (percentage) => {
  if (percentage >= 100) return 'bg-green-500';
  if (percentage >= 75) return 'bg-emerald-500';
  if (percentage >= 50) return 'bg-blue-500';
  if (percentage >= 25) return 'bg-amber-500';
  return 'bg-slate-400';
};

/**
 * Get progress status
 */
const getProgressStatus = (percentage) => {
  if (percentage >= 100) return { label: 'Complete', icon: CheckCircle2, color: 'text-green-500' };
  if (percentage >= 75) return { label: 'Almost Done', icon: Clock, color: 'text-emerald-500' };
  if (percentage >= 50) return { label: 'In Progress', icon: Clock, color: 'text-blue-500' };
  if (percentage >= 25) return { label: 'Started', icon: Clock, color: 'text-amber-500' };
  return { label: 'Not Started', icon: AlertCircle, color: 'text-slate-400' };
};

/**
 * Simple progress bar
 */
export function SimpleProgressBar({ 
  value = 0, 
  max = 100, 
  size = 'default',
  showPercentage = true,
  className 
}) {
  const percentage = Math.min(100, Math.max(0, (value / max) * 100));
  const progressColor = getProgressColor(percentage);

  const heightClasses = {
    sm: 'h-1',
    default: 'h-2',
    lg: 'h-3',
    xl: 'h-4',
  };

  return (
    <div className={cn('w-full', className)} data-testid="simple-progress-bar">
      {showPercentage && (
        <div className="flex justify-end mb-1">
          <span className="text-xs text-muted-foreground">{Math.round(percentage)}%</span>
        </div>
      )}
      <div className={cn('w-full rounded-full bg-muted overflow-hidden', heightClasses[size])}>
        <div
          className={cn('h-full rounded-full transition-all duration-500 ease-out', progressColor)}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

/**
 * Labeled progress bar with status
 */
export function LabeledProgressBar({ 
  value = 0, 
  max = 100, 
  label,
  showStatus = true,
  showPercentage = true,
  size = 'default',
  className 
}) {
  const percentage = Math.min(100, Math.max(0, (value / max) * 100));
  const progressColor = getProgressColor(percentage);
  const status = getProgressStatus(percentage);
  const StatusIcon = status.icon;

  const heightClasses = {
    sm: 'h-1.5',
    default: 'h-2',
    lg: 'h-3',
  };

  return (
    <div className={cn('w-full', className)} data-testid="labeled-progress-bar">
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2">
          {label && <span className="text-sm font-medium">{label}</span>}
          {showStatus && (
            <span className={cn('flex items-center gap-1 text-xs', status.color)}>
              <StatusIcon className="h-3 w-3" />
              {status.label}
            </span>
          )}
        </div>
        {showPercentage && (
          <span className="text-sm font-medium">{Math.round(percentage)}%</span>
        )}
      </div>
      <div className={cn('w-full rounded-full bg-muted overflow-hidden', heightClasses[size])}>
        <div
          className={cn('h-full rounded-full transition-all duration-500 ease-out', progressColor)}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

/**
 * Segmented progress bar (for multi-section documents)
 */
export function SegmentedProgressBar({ 
  segments = [], 
  size = 'default',
  showLegend = false,
  className 
}) {
  const total = segments.reduce((sum, s) => sum + s.value, 0);

  const heightClasses = {
    sm: 'h-2',
    default: 'h-3',
    lg: 'h-4',
  };

  const segmentColors = {
    completed: 'bg-green-500',
    inProgress: 'bg-blue-500',
    notStarted: 'bg-slate-300 dark:bg-slate-600',
    needsRevision: 'bg-orange-500',
  };

  return (
    <div className={cn('w-full', className)} data-testid="segmented-progress-bar">
      <div className={cn('w-full rounded-full bg-muted overflow-hidden flex', heightClasses[size])}>
        {segments.map((segment, index) => {
          const width = total > 0 ? (segment.value / total) * 100 : 0;
          return (
            <div
              key={segment.id || index}
              className={cn(
                'h-full transition-all duration-300',
                segmentColors[segment.type] || 'bg-slate-400',
                index === 0 && 'rounded-l-full',
                index === segments.length - 1 && 'rounded-r-full'
              )}
              style={{ width: `${width}%` }}
              title={`${segment.label}: ${segment.value}`}
            />
          );
        })}
      </div>
      {showLegend && (
        <div className="flex flex-wrap gap-4 mt-2">
          {segments.map((segment, index) => (
            <div key={segment.id || index} className="flex items-center gap-1.5">
              <div className={cn('w-3 h-3 rounded-full', segmentColors[segment.type] || 'bg-slate-400')} />
              <span className="text-xs text-muted-foreground">
                {segment.label}: {segment.value}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Circular progress indicator
 */
export function CircularProgress({ 
  value = 0, 
  max = 100, 
  size = 'default',
  showPercentage = true,
  showStatus = false,
  className 
}) {
  const percentage = Math.min(100, Math.max(0, (value / max) * 100));
  const status = getProgressStatus(percentage);

  const sizeConfig = {
    sm: { container: 'w-12 h-12', text: 'text-xs', stroke: 3 },
    default: { container: 'w-16 h-16', text: 'text-sm', stroke: 4 },
    lg: { container: 'w-20 h-20', text: 'text-base', stroke: 5 },
    xl: { container: 'w-24 h-24', text: 'text-lg', stroke: 6 },
  };

  const config = sizeConfig[size];
  const radius = 45;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  // Get color class for stroke
  const strokeColor = percentage >= 100 ? 'text-green-500' : 
                      percentage >= 75 ? 'text-emerald-500' : 
                      percentage >= 50 ? 'text-blue-500' : 
                      percentage >= 25 ? 'text-amber-500' : 'text-slate-400';

  return (
    <div className={cn('relative flex items-center justify-center', config.container, className)} data-testid="circular-progress">
      <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
        {/* Background circle */}
        <circle
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={config.stroke}
          className="text-muted"
        />
        {/* Progress circle */}
        <circle
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={config.stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          className={strokeColor}
          style={{ transition: 'stroke-dashoffset 0.5s ease-out' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        {showPercentage && (
          <span className={cn('font-bold', config.text)}>{Math.round(percentage)}%</span>
        )}
        {showStatus && (
          <span className="text-[10px] text-muted-foreground">{status.label}</span>
        )}
      </div>
    </div>
  );
}

/**
 * Document progress card
 */
export function DocumentProgressCard({ 
  title,
  progress = 0,
  sectionsCompleted = 0,
  totalSections = 0,
  className 
}) {
  const percentage = Math.min(100, Math.max(0, progress));
  const status = getProgressStatus(percentage);
  const StatusIcon = status.icon;

  return (
    <div className={cn('p-4 rounded-lg border bg-card', className)} data-testid="document-progress-card">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h4 className="font-medium text-sm">{title}</h4>
          <p className="text-xs text-muted-foreground mt-0.5">
            {sectionsCompleted} of {totalSections} sections complete
          </p>
        </div>
        <div className={cn('flex items-center gap-1', status.color)}>
          <StatusIcon className="h-4 w-4" />
          <span className="text-xs font-medium">{status.label}</span>
        </div>
      </div>
      <SimpleProgressBar value={percentage} showPercentage={false} size="lg" />
      <div className="flex justify-end mt-1">
        <span className="text-sm font-semibold">{Math.round(percentage)}%</span>
      </div>
    </div>
  );
}

/**
 * Main ProgressBar component with variant prop
 */
export function ProgressBar({ 
  value = 0, 
  max = 100,
  variant = 'simple',
  label,
  showStatus = false,
  showPercentage = true,
  size = 'default',
  segments,
  showLegend = false,
  className 
}) {
  switch (variant) {
    case 'labeled':
      return (
        <LabeledProgressBar 
          value={value} 
          max={max} 
          label={label}
          showStatus={showStatus}
          showPercentage={showPercentage}
          size={size}
          className={className}
        />
      );
    case 'segmented':
      return (
        <SegmentedProgressBar 
          segments={segments || []}
          size={size}
          showLegend={showLegend}
          className={className}
        />
      );
    case 'circular':
      return (
        <CircularProgress 
          value={value} 
          max={max}
          size={size}
          showPercentage={showPercentage}
          showStatus={showStatus}
          className={className}
        />
      );
    case 'simple':
    default:
      return (
        <SimpleProgressBar 
          value={value} 
          max={max}
          size={size}
          showPercentage={showPercentage}
          className={className}
        />
      );
  }
}

export default ProgressBar;
